import { useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { RefreshCw, ShieldAlert } from "lucide-react";

import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { useTransaction } from "@/features/wallet/api/use-transaction";
import { TRANSIENT_TRANSACTION_STATUSES } from "@/features/wallet/api/dto";
import { TRANSACTION_DISPUTE_OPENING_WINDOW_MINUTES } from "@/features/wallet/api/enums";
import {
  OpenTransactionDisputeDialog,
  TransactionDetailCard,
  TransactionOrchestrationActions,
  TransactionWorkerByReferenceActions,
} from "@/features/wallet/components";
import { useTransactionsTranslation } from "@/features/wallet/i18n/use-transactions-translation";

/**
 * Fenêtre d'ouverture d'une contestation (guide v3.1 —
 * `Disputes__OpeningWindowMinutes`, 1 h après l'envoi). L'appartenance
 * « expéditeur » n'est pas vérifiable côté front (le DTO n'expose pas le
 * titulaire connecté) : la visibilité se limite à COMPLETED + fenêtre, le
 * serveur tranche (404 si l'appelant n'est pas l'expéditeur).
 */
const isWithinDisputeWindow = (
  transaction: { status: string; initiatedAt?: string; completedAt?: string | null }
): boolean => {
  if (transaction.status !== "COMPLETED") return false;
  const reference = transaction.completedAt ?? transaction.initiatedAt;
  if (!reference) return false;
  const elapsedMs = Date.now() - new Date(reference).getTime();
  return (
    elapsedMs >= 0 &&
    elapsedMs <= TRANSACTION_DISPUTE_OPENING_WINDOW_MINUTES * 60 * 1000
  );
};

/**
 * Détail / reçu d'une transaction (`GET /Transactions/{id}`) — suivi de
 * statut du flux §5.3 : polling automatique tant que le statut est
 * transitoire (PENDING / AWAITING_LEDGER), puis actions d'orchestration
 * manuelles (confirm-ledger / mark-failed — Kafka non câblé). Les hooks de
 * mutation invalident/priment les queries après action.
 */
export function TransactionDetailPage() {
  const { t } = useTransactionsTranslation();
  const { transactionId = "" } = useParams();
  const { data: transaction, isLoading, error, refetch } =
    useTransaction(transactionId);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const backButton = (
    <PageBackButton to="/app/wallet/transactions" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !transaction) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const isTransient = TRANSIENT_TRANSACTION_STATUSES.includes(transaction.status);
  const canDispute = isWithinDisputeWindow(transaction);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span" className="font-mono text-xl">
              {transaction.reference}
            </NoTranslate>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t("reload")}
          </Button>
          {canDispute ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisputeOpen(true)}
            >
              <ShieldAlert className="mr-2 h-4 w-4" />
              {t("wallet.disputes.open.action")}
            </Button>
          ) : null}
          <TransactionOrchestrationActions transaction={transaction} />
        </div>
      </div>

      {isTransient ? (
        <p className="text-sm text-muted-foreground">
          {t("wallet.transactions.detail.polling")}{" "}
          <Link
            to="/app/ledger/journal-entries"
            className="text-primary underline-offset-4 hover:underline"
          >
            {t("wallet.transactions.detail.ledgerLink")}
          </Link>
        </p>
      ) : null}

      <TransactionDetailCard transaction={transaction} />

      <TransactionWorkerByReferenceActions transaction={transaction} />

      <OpenTransactionDisputeDialog
        transaction={transaction}
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
      />
    </section>
  );
}
